import * as DialogPrimitive from '@radix-ui/react-dialog';
import { forwardRef, ReactNode, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { restrictedZIndexes } from '../../utils';
import { useText } from '../../utils/hooks/use_text';
import { IconButton } from '../icon_button';
import { useCellulaProviderContext } from '../provider';
import { theme } from './dialog_modal.theme';

export type DialogModalSize = 'default' | 'large';

const overlayShow = keyframes`
	from {
		opacity: 0;
	}
  to {
		opacity: 1;
	}
`;

const contentShow = keyframes`
	from {
		opacity: 0;
		transform: translate(-50%, -40%) scale(0.96);
	}
  to {
		opacity: 1;
		transform: translate(-50%, -50%) scale(1);
	}
`;

const elements = {
	overlay: styled(DialogPrimitive.Overlay)`
		background: rgba(0, 0, 0, 0.3);
		position: fixed;
		inset: 0;
		animation-duration: 500ms;
		animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
		will-change: opacity;
		/* Needs to use a high z-index to have it appear on top of other elements */
		z-index: ${restrictedZIndexes.DANGER};

		@media (prefers-reduced-motion: no-preference) {
			animation-name: ${overlayShow};
		}
	`,
	content: styled(DialogPrimitive.Content)<{ $size: DialogModalSize }>`
		display: flex;
		background: ${theme.getValue('background')};
		border-radius: ${theme.getValue('borderRadius')};
		box-shadow: ${theme.getValue('boxShadow')};
		z-index: ${restrictedZIndexes.normal};

		position: fixed;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: ${({ $size }) => ($size === 'large' ? 'min(96vw, 1200px)' : '90vw')};
		max-width: ${({ $size }) => ($size === 'large' ? 'none' : '550px')};
		max-height: ${({ $size }) => ($size === 'large' ? '92vh' : '85vh')};
		animation-duration: 250ms;
		animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
		/* Needs to use a high z-index to have it appear on top of other elements */
		z-index: ${restrictedZIndexes.DANGER};

		&:focus {
			outline: none;
		}
		@media (prefers-reduced-motion: no-preference) {
			animation-name: ${contentShow};
		}
	`,
	inner: styled.div`
		display: flex;
		flex-direction: column;
		padding: ${theme.getValue('padding')};
		max-height: 100%;
		gap: ${theme.getValue('gap')};
		width: 100%;
	`,
	innerContent: styled.div`
		display: flex;
		flex-direction: column;
		flex-shrink: 1;
		overflow: auto;
		gap: ${theme.getValue('gap')};
		width: calc(100% + ${theme.getValue('padding')});
		padding: calc(${theme.getValue('gap')} / 2) calc(${theme.getValue('padding')} / 2);
		margin: calc(${theme.getValue('gap')} / -2) calc(${theme.getValue('padding')} / -2);
	`,
	closeCornerButton: styled.div`
		position: absolute;
		right: calc(${theme.getValue('padding')} / 2);
		top: calc(${theme.getValue('padding')} / 2);
	`,
};

interface WithTrigger {
	trigger: ReactNode;
	open?: never;
}
interface WithoutTrigger {
	trigger?: never;
	open: boolean;
}
type WithTriggerProps = WithTrigger | WithoutTrigger;

export type DialogModalRootProps = {
	abortText?: string;
	defaultOpen?: boolean;
	closeable?: boolean;
	children?: ReactNode;
	onOpenChange?: (open: boolean) => void;
	onOpenAutoFocus?: DialogPrimitive.DialogContentProps['onOpenAutoFocus'];
	onCloseAutoFocus?: DialogPrimitive.DialogContentProps['onCloseAutoFocus'];
	onEscapeKeyDown?: DialogPrimitive.DialogContentProps['onEscapeKeyDown'];
	onPointerDownOutside?: DialogPrimitive.DialogContentProps['onPointerDownOutside'];
	onInteractOutside?: DialogPrimitive.DialogContentProps['onInteractOutside'];
	title?: string;
	description?: string;
	/**
	 * Use `large` for content-dense dialogs such as editors.
	 */
	size?: DialogModalSize;
} & WithTriggerProps;

export const DialogModalRoot = forwardRef<HTMLDivElement, DialogModalRootProps>(function DialogModalRoot(
	{
		defaultOpen = false,
		closeable = true,
		trigger,
		open: controlledOpen,
		abortText,
		children,
		onOpenChange,
		onOpenAutoFocus,
		onCloseAutoFocus,
		onEscapeKeyDown,
		onPointerDownOutside,
		onInteractOutside,
		title,
		description,
		size = 'default',
	}: DialogModalRootProps,
	ref,
): JSX.Element {
	const staticText = useText();
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
	const { portalElement } = useCellulaProviderContext();
	const open = controlledOpen ?? uncontrolledOpen;

	function handleOpenChange(open: boolean): void {
		if (controlledOpen == null) {
			setUncontrolledOpen(open);
		}
		onOpenChange?.(open);
	}

	return (
		<DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
			{trigger != null ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}

			<DialogPrimitive.Portal container={portalElement}>
				<elements.overlay />
				<elements.content
					ref={ref}
					$size={size}
					data-size={size}
					onOpenAutoFocus={onOpenAutoFocus}
					onCloseAutoFocus={onCloseAutoFocus}
					onEscapeKeyDown={onEscapeKeyDown}
					onPointerDownOutside={onPointerDownOutside}
					onInteractOutside={onInteractOutside}
				>
					<elements.inner>
						{title && <DialogPrimitive.Title>{title}</DialogPrimitive.Title>}
						{description && <DialogPrimitive.Description>{description}</DialogPrimitive.Description>}
						{children}

						{closeable ? (
							<elements.closeCornerButton>
								<DialogPrimitive.Close asChild>
									<IconButton variant='neutral' text={abortText ?? staticText('Common.Close')} icon='x' size='medium' />
								</DialogPrimitive.Close>
							</elements.closeCornerButton>
						) : null}
					</elements.inner>
				</elements.content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
});
